import React, { Fragment, useEffect } from "react";
import { Link } from "gatsby";
import Helmet from "react-helmet";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";

import TagManager from "react-gtm-module";

const Layout = ({ slug, seo, date, children, scroll, location, company }) => {
  const {
    noIndex,
    pageTitle,
    pageDescription,
    description,
    shareImage,
    isBlogPost,
    ogUrl,
    ogImage,
    twitterImage,
  } = seo;

  const publishDate = isBlogPost ? date : "";

  const params = new URLSearchParams(location.search),
    UTCCampaign = params.get("utc_campaign"),
    UTCContent = params.get("utc_content"),
    UTCMedium = params.get("utc_medium"),
    UTCSource = params.get("utc_source");

  if (UTCCampaign !== null && UTCCampaign !== "") {
    localStorage.setItem("utc_campaign", UTCCampaign);
  }

  if (UTCContent !== null && UTCContent !== "") {
    localStorage.setItem("utc_content", UTCContent);
  }

  if (UTCMedium !== null && UTCMedium !== "") {
    localStorage.setItem("utc_medium", UTCMedium);
  }

  if (UTCSource !== null && UTCSource !== "") {
    localStorage.setItem("utc_source", UTCSource);
  }

  const shareImageUrl = ogImage
    ? `${ogImage?.file?.url}`
    : `${shareImage?.url}`;

  const schemaOrgJSONLD = [
    {
      "@context": "http://schema.org",
      "@type": "WebSite",
      url: ogUrl,
      name: company.name,
      alternateName: "",
    },

    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      logo: "https://images.ctfassets.net/es6h7ygxiqjj/6zPKefmoqcKz160A1miE59/23fed711032d59c8b93cd141c0b0945a/moonello-light-logo.png?w=250&h=36&q=50&fm=png",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Royal Oak",
        addressRegion: "MI",
        postalCode: "48967",
        streetAddress: "220 S. Main Street",
      },
      description:
        "Elevate Your Michigan Business with Software Solutions & Marketing Strategies",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5",
        reviewCount: "11",
      },
      name: company.name,
      email: company.email,
      openingHours: ["Mo-Fr 9:00-17:00"],
      telephone: company.phone,
      url: "https://moonello.com",
      sameAs: [
        "https://www.facebook.com/wearemoonello",
        "https://www.twitter.com/wearemoonello",
        "https://www.instagram.com/wearemoonello",
        "https://www.tiktok.com/@wearemoonello",
        "https://www.linkedin.com/company/wearemoonello",
        "https://www.youtube.com/@wearemoonello",
      ],
    },
  ];

  if (isBlogPost) {
    // const shareUrl = ogUrl;
    schemaOrgJSONLD.push(
      {
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Moonello Blog",
            item: "https://moonello.com/blog/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: pageTitle,
            item: ogUrl,
          },
        ],
      },
      {
        "@context": "http://schema.org",
        "@type": "BlogPosting",
        url: ogUrl,
        name: pageTitle,
        keywords: pageTitle.split(" "),
        alternateName: "",
        headline: pageTitle,
        image: {
          "@type": "ImageObject",
          url: shareImageUrl,
          width: 1500,
          height: 1000,
        },
        author: {
          "@type": "Company",
          name: "@wearemoonello",
          url: ogUrl,
        },
        publisher: {
          "@type": "Organization",
          name: "Moonello",
          url: ogUrl,
        },
        datePublished: publishDate,
        mainEntityOfPage: ogUrl,
        isPartOf: {
          "@type": "Blog",
          "@id": "https://moonello.com/blog/",
          name: "Moonello Blog",
          publisher: {
            "@type": "Organization",
            "@id": "https://moonello.com",
            name: "Moonello",
          },
        },
      }
    );
  } else {
    // const shareUrl = slug === "home" ? "" : "" + slug;
    schemaOrgJSONLD.push({
      "@context": "http://schema.org",
      "@type": "WebPage",
      url: ogUrl,
      name: pageTitle,
    });
  }

  const handleAcceptCookie = () => {
    TagManager.initialize({ gtmId: process.env.GATSBY_GOOGLE_TAG_MANAGER });
  };

  const handleDeclineCookie = () => {};

  useEffect(() => {
    const isConsent = getCookieConsentValue();
    if (isConsent === "true") {
      handleAcceptCookie();
    }
  }, []);

  return (
    <Fragment>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="./src/images/apple-touch-icon.png"
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link
          rel="preload"
          href="https://fonts.googleapis.com/css2?family=Lexend:wght@100;200;300;400;900&display=swap"
          as="style"
          onload="this.onload=null;this.rel='stylesheet'"
        />
        <noscript>
          {`
          <link
          href="https://fonts.googleapis.com/css2?family=Lexend:wght@100;200;300;400;900&display=swap"
          rel="stylesheet"
          type="text/css"
        />
          `}
        </noscript>
        <link rel="manifest" href="./src/images/site.webmanifest" />
        {noIndex && <link rel="robots" content="noindex" />}
        <title>{pageTitle} | Moonello</title>
        <meta name="description" content={pageDescription || description} />
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>
        <meta name="facebook-domain-verification" content="" />
        <meta property="og:type" content={isBlogPost ? "article" : "website"} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content={pageDescription || description}
        />
        <meta property="og:url" content={ogUrl} />
        <meta property="og:image" content={shareImageUrl} />
        <meta property="og:image:width" content="1500" />
        <meta property="og:image:height" content="1000" />
        <meta property="og:image:alt" content={pageTitle} />
        <meta property="og:site_name" content="Moonello" />
        <meta property="og:see_also" content="moonelo" />
        <meta property="og:see_also" content="monello" />
        <meta property="og:see_also" content="" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:description"
          content={pageDescription || description}
        />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:site" content="" />
        <meta name="twitter:image" content={shareImageUrl} />
        <meta name="twitter:creator" content="@wearemoonello" />
        <meta name="twitter:url" content={ogUrl} />
        <meta name="twitter:label1" content="Est. reading time" />
        <meta name="twitter:data1" content="2 minutes" />
        <meta property="twitter:image:alt" content={pageTitle} />
      </Helmet>
      <main className={`${scroll && "noscroll"}`}>{children}</main>

      <CookieConsent
        containerClasses="cookie-consent"
        location="bottom"
        acceptOnScroll={true}
        acceptOnScrollPercentage={10}
        onAccept={handleAcceptCookie}
        onDecline={handleDeclineCookie}
        buttonStyle={{
          backgroundColor: "lightgray",
          padding: "12px 20px",
          color: "black",
          fontSize: "13px",
          borderRadius: "7px",
          fontFamily: "Lexend",
        }}
        declineButtonStyle={{ background: "none", color: "black" }}
        buttonText="I understand"
        // enableDeclineButton
        // declineButtonText="Decline"
        style={{ backgroundColor: "white", color: "black" }}
        flipButtons={false}
      >
        We use cookies on our website to ensure that we provide the best
        experience possible. To view our cookies policy, please visit our{" "}
        <Link to="/privacy-policy">
          <span style={{ textDecoration: "underline" }}>Privacy Policy</span>
        </Link>{" "}
        page.
      </CookieConsent>
    </Fragment>
  );
};

export default Layout;
